

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { asset } from '@/lib/asset';

interface PackCardProps {
  id: string;
  name: string;
  description: string;
  price: number;
  priceType: 'USD' | 'COINS' | 'GEMS';
  cardCount: number;
  features: string[];
  guaranteedRarity: string | null;
  allRarePlus: boolean;
  /**
   * Series supply sold-out (guaranteed SKUs when Rare+ supply runs out).
   * Catalog-driven (`soldOut` on GET /api/packs) — no client-side supply math.
   */
  soldOut?: boolean;
  /** Footer series line; defaults to the NBA copper line when omitted. */
  seriesText?: string;
  onPurchase: (packId: string) => void;
  isLoading?: boolean;
}

const PACK_ACCENTS: Record<string, { border: string; sealFilter: string }> = {
  'standard': {
    border: 'border-[#6B1F2A]/40',
    // Deep burgundy: push red, desaturate slightly, darken
    sealFilter: 'hue-rotate(-20deg) saturate(3) brightness(0.5)',
  },
  'court-side': {
    border: 'border-[#1A2F5A]/40',
    // Navy blue: shift hue, high saturation, very dark
    sealFilter: 'hue-rotate(190deg) saturate(4) brightness(0.45)',
  },
  'premium': {
    border: 'border-verdigris/50',
    sealFilter: 'hue-rotate(190deg) saturate(4) brightness(0.45)',
  },
  'prime': {
    border: 'border-gold/50',
    // Natural gold/copper — no filter needed
    sealFilter: '',
  },
  // NFL Card Leagues tiers — ruby wax, deepening with tier; All-Pro goes gold.
  'nfl_rookie': {
    border: 'border-[#9B111E]/30',
    sealFilter: 'hue-rotate(-15deg) saturate(2.5) brightness(0.55)',
  },
  'nfl_pro': {
    border: 'border-[#9B111E]/50',
    sealFilter: 'hue-rotate(-15deg) saturate(3.5) brightness(0.45)',
  },
  'nfl_allpro': {
    border: 'border-gold/50',
    sealFilter: '',
  },
};

export function PackCard({
  id,
  name,
  description,
  price,
  priceType,
  cardCount,
  features,
  guaranteedRarity,
  allRarePlus,
  soldOut,
  seriesText,
  onPurchase,
  isLoading,
}: PackCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const accent = PACK_ACCENTS[id] || PACK_ACCENTS['standard'];

  const formatPrice = () => {
    if (priceType === 'USD') return `$${price}`;
    if (priceType === 'COINS') return `${price.toLocaleString()} Coins`;
    return `${price.toLocaleString()} Gems`;
  };

  return (
    <motion.div
      className={cn(
        'relative rounded-2xl overflow-hidden cursor-pointer',
        'bg-parchment border',
        accent.border,
        'shadow-xl transition-all duration-300'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Subtle sparkle on hover */}
      {isHovered && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Sparkles className="absolute top-4 right-4 h-5 w-5 text-gold/30 animate-pulse" />
        </motion.div>
      )}

      <div className="relative p-6 flex flex-col h-full min-h-[400px]">
        {/* Wax Seal Emblem */}
        <div className="flex justify-center mb-4">
          <Image
            src={asset('assets/fantasy_prime_seal.webp')}
            alt="Seal"
            width={96}
            height={96}
            className="object-contain drop-shadow-md"
            style={accent.sealFilter ? { filter: accent.sealFilter } : undefined}
          />
        </div>

        {/* Pack Name */}
        <h3 className="text-xl font-serif font-bold text-surface text-center mb-1">
          {name}
        </h3>

        {/* Ornamental divider */}
        <div className="text-center text-parchment-border tracking-[0.5em] text-[8px] mb-2">
          · · ·
        </div>

        {/* Description */}
        <p className="text-[#7D7568] text-sm text-center mb-4 flex-grow">
          {description}
        </p>

        {/* Card Count */}
        <p className="text-center text-surface/60 text-sm font-serif mb-3">
          {cardCount} Cards Struck
        </p>

        {/* Badges */}
        <div className="flex justify-center gap-2 mb-4 flex-wrap">
          {soldOut && (
            <span className="px-3 py-1 rounded-full bg-[#7D7568]/10 text-[#7D7568] text-[10px] font-bold tracking-wider border border-[#7D7568]/30">
              SOLD OUT — SERIES 2 COMING
            </span>
          )}
          {allRarePlus && (
            <span className="px-3 py-1 rounded-full bg-verdigris/10 text-verdigris text-[10px] font-bold tracking-wider border border-verdigris/30">
              ALL RARE+
            </span>
          )}
          {guaranteedRarity && !allRarePlus && (
            <span className="px-3 py-1 rounded-full bg-gold/10 text-gold text-[10px] font-bold tracking-wider border border-gold/30">
              {guaranteedRarity}+ GUARANTEED
            </span>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-1.5 mb-5">
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#7D7568]">
              <Image src={asset('assets/icons/lightning.webp')} alt="" width={14} height={14} />
              {feature}
            </li>
          ))}
        </ul>

        {/* Series text */}
        <p className="series-text text-center mb-4">
          {seriesText ?? 'NBA · COPPER SERIES · 2026'}
        </p>

        {/* Price & Button */}
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-[#7D7568] text-sm">Price</span>
            <span className="text-xl font-bold text-surface font-mono">{formatPrice()}</span>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={() => onPurchase(id)}
            disabled={isLoading || soldOut}
          >
            {soldOut ? 'Sold Out' : isLoading ? 'Processing...' : 'Purchase'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
